/**
 * useMessageFilters — Multi-value message filter hook
 *
 * Per-field include/exclude model: each field has a values array and a mode
 * ('include' or 'exclude'). All values in a field share the same mode.
 *
 * Pure functions (matchesFilters, parseFiltersFromParams, filtersToParams,
 * filtersToApiParams) are exported for use without the hook.
 */

import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import type { OrgMessage } from '../../common/entities';
import type { Speaker } from '../../api/server/speakers';

// =============================================================================
// Types
// =============================================================================

export type FilterMode = 'include' | 'exclude';

export interface FieldFilter {
  values: string[];
  mode: FilterMode;
}

export type FilterField =
  | 'channels'
  | 'sourceTypes'
  | 'deviceIds'
  | 'feedIds'
  | 'talkgroups'
  | 'talkgroupTags'
  | 'radioIds'
  | 'speakerIds'
  | 'tags';

export interface MessageFilters {
  channels: FieldFilter;
  sourceTypes: FieldFilter;
  deviceIds: FieldFilter;
  feedIds: FieldFilter;
  talkgroups: FieldFilter;
  talkgroupTags: FieldFilter;
  radioIds: FieldFilter;
  speakerIds: FieldFilter;
  tags: FieldFilter;
  emergency: boolean;
}

// =============================================================================
// Constants
// =============================================================================

export const DEFAULT_FIELD: FieldFilter = { values: [], mode: 'include' };

/** Create a fresh MessageFilters with all fields at default. */
export function createDefaultFilters(): MessageFilters {
  return {
    channels: { ...DEFAULT_FIELD },
    sourceTypes: { ...DEFAULT_FIELD },
    deviceIds: { ...DEFAULT_FIELD },
    feedIds: { ...DEFAULT_FIELD },
    talkgroups: { ...DEFAULT_FIELD },
    talkgroupTags: { ...DEFAULT_FIELD },
    radioIds: { ...DEFAULT_FIELD },
    speakerIds: { ...DEFAULT_FIELD },
    tags: { ...DEFAULT_FIELD },
    emergency: false
  };
}

export const DEFAULT_FILTERS: MessageFilters = createDefaultFilters();

/** All filter field names (excludes emergency which is boolean). */
const FILTER_FIELDS: FilterField[] = [
  'channels',
  'sourceTypes',
  'deviceIds',
  'feedIds',
  'talkgroups',
  'talkgroupTags',
  'radioIds',
  'speakerIds',
  'tags'
];

/**
 * Fields that support exclude mode (have server-side exclude param counterparts).
 * feedIds and talkgroups do not have exclude support.
 */
export const TOGGLEABLE_FIELDS = new Set<FilterField>([
  'channels',
  'sourceTypes',
  'deviceIds',
  'talkgroupTags',
  'radioIds',
  'speakerIds',
  'tags'
]);

/** FilterField → canonical URL param name (singular) */
const FIELD_TO_URL_PARAM: Record<FilterField, string> = {
  channels: 'channel_id',
  sourceTypes: 'source_type',
  deviceIds: 'device_id',
  feedIds: 'feed_id',
  talkgroups: 'talkgroup',
  talkgroupTags: 'talkgroup_tag',
  radioIds: 'radio_id',
  speakerIds: 'speaker_id',
  tags: 'tag'
};

/** Canonical URL param → FilterField (reverse of FIELD_TO_URL_PARAM) */
const URL_PARAM_TO_FIELD: Record<string, FilterField> = {};
for (const [field, param] of Object.entries(FIELD_TO_URL_PARAM)) {
  URL_PARAM_TO_FIELD[param] = field as FilterField;
}

/** Plural URL param aliases → singular canonical form */
const URL_PARAM_ALIASES: Record<string, string> = {
  source_types: 'source_type',
  device_ids: 'device_id',
  feed_ids: 'feed_id',
  talkgroup_tags: 'talkgroup_tag',
  radio_ids: 'radio_id',
  speaker_ids: 'speaker_id'
};

/** Old-format exclude param → FilterField mapping */
const OLD_EXCLUDE_PARAM_TO_FIELD: Record<string, FilterField> = {
  exclude_source_type: 'sourceTypes',
  exclude_device_id: 'deviceIds',
  exclude_talkgroup_tag: 'talkgroupTags',
  exclude_radio_id: 'radioIds',
  exclude_speaker_id: 'speakerIds',
  exclude_tag: 'tags'
};

/** Old-format plural exclude aliases → singular */
const OLD_EXCLUDE_ALIASES: Record<string, string> = {
  exclude_source_types: 'exclude_source_type',
  exclude_device_ids: 'exclude_device_id',
  exclude_talkgroup_tags: 'exclude_talkgroup_tag',
  exclude_radio_ids: 'exclude_radio_id',
  exclude_speaker_ids: 'exclude_speaker_id',
  exclude_tags: 'exclude_tag'
};

/** FilterField → server API include param name (plural) */
const FIELD_TO_API_INCLUDE: Record<FilterField, string> = {
  channels: 'channel_ids',
  sourceTypes: 'source_types',
  deviceIds: 'device_ids',
  feedIds: 'feed_ids',
  talkgroups: 'talkgroups',
  talkgroupTags: 'talkgroup_tags',
  radioIds: 'radio_ids',
  speakerIds: 'speaker_ids',
  tags: 'tags'
};

/** FilterField → server API exclude param name (plural) */
const FIELD_TO_API_EXCLUDE: Partial<Record<FilterField, string>> = {
  channels: 'exclude_channel_ids',
  sourceTypes: 'exclude_source_types',
  deviceIds: 'exclude_device_ids',
  talkgroupTags: 'exclude_talkgroup_tags',
  radioIds: 'exclude_radio_ids',
  speakerIds: 'exclude_speaker_ids',
  tags: 'exclude_tags'
};

// =============================================================================
// matchesFilters — pure, client-side message matching
// =============================================================================

/** Sentinel value for "messages where this field is NULL/empty" */
const NONE_SENTINEL = '__none__';

/** Check a single scalar field against a FieldFilter. Supports __none__ sentinel for NULL matching. */
function checkScalarField(msgValue: string | undefined | null, filter: FieldFilter): boolean {
  if (filter.values.length === 0) return true;

  if (filter.mode === 'include') {
    // __none__ matches null/empty values
    if (!msgValue && filter.values.includes(NONE_SENTINEL)) return true;
    return filter.values.includes(msgValue ?? '');
  }
  // exclude: fail if value matches any exclude value
  // __none__ in exclude means exclude null/empty values
  if (!msgValue) return !filter.values.includes(NONE_SENTINEL);
  return !filter.values.includes(msgValue);
}

/** Returns true if the message passes all active filters. */
export const matchesFilters = (
  msg: OrgMessage,
  filters: MessageFilters,
  radioIdToSpeakerMap?: Record<string, Speaker>
): boolean => {
  // --- Scalar field checks ---
  if (!checkScalarField(msg.channel_id, filters.channels)) return false;
  if (!checkScalarField(msg.source_type, filters.sourceTypes)) return false;
  if (!checkScalarField(msg.device_id, filters.deviceIds)) return false;
  if (!checkScalarField(msg.feed_id, filters.feedIds)) return false;
  if (!checkScalarField(msg.talkgroup, filters.talkgroups)) return false;
  if (!checkScalarField(msg.talkgroup_tag, filters.talkgroupTags)) return false;
  if (!checkScalarField(msg.radio_id, filters.radioIds)) return false;

  // --- Tags (array overlap) ---
  if (filters.tags.values.length > 0) {
    const msgTags = Array.isArray(msg.tags) ? msg.tags : [];
    if (filters.tags.mode === 'include') {
      if (!filters.tags.values.some((t) => msgTags.includes(t))) return false;
    } else {
      if (filters.tags.values.some((t) => msgTags.includes(t))) return false;
    }
  }

  // --- Emergency ---
  if (filters.emergency && !msg.emergency) return false;

  // --- Speaker (with radio_id inference) ---
  if (filters.speakerIds.values.length > 0) {
    const directMatch = !!msg.speaker_id && filters.speakerIds.values.includes(msg.speaker_id);
    const inferredSpeakerId =
      !msg.speaker_id && !!msg.radio_id && radioIdToSpeakerMap?.[msg.radio_id]?.speaker_id;

    if (filters.speakerIds.mode === 'include') {
      const inferredMatch =
        !!inferredSpeakerId && filters.speakerIds.values.includes(inferredSpeakerId);
      if (!directMatch && !inferredMatch) return false;
    } else {
      const directExclude = directMatch;
      const inferredExclude =
        !!inferredSpeakerId && filters.speakerIds.values.includes(inferredSpeakerId);
      if (directExclude || inferredExclude) return false;
    }
  }

  return true;
};

// =============================================================================
// URL <-> Filters serialization (pure functions)
// =============================================================================

/** Parse URLSearchParams into a MessageFilters object. Handles both new and old formats. */
export function parseFiltersFromParams(params: URLSearchParams): MessageFilters {
  const filters = createDefaultFilters();

  // --- Read include values (singular canonical params) ---
  for (const [urlParam, field] of Object.entries(URL_PARAM_TO_FIELD)) {
    const value = params.get(urlParam);
    const modeParam = params.get(`${urlParam}_mode`);
    const mode = modeParam === 'exclude' && TOGGLEABLE_FIELDS.has(field) ? 'exclude' : 'include';

    if (value) {
      filters[field] = {
        values: value.split(',').filter(Boolean),
        mode
      };
    } else if (mode === 'exclude') {
      // Preset mode with no values yet
      filters[field] = { values: [], mode: 'exclude' };
    }
  }

  // --- Read plural aliases (fallback, only if singular was empty) ---
  for (const [pluralParam, singularParam] of Object.entries(URL_PARAM_ALIASES)) {
    const field = URL_PARAM_TO_FIELD[singularParam];
    if (filters[field].values.length === 0) {
      const value = params.get(pluralParam);
      if (value) {
        filters[field] = {
          values: value.split(',').filter(Boolean),
          mode: 'include'
        };
      }
    }
  }

  // --- Old-format exclude params (backward compat) ---
  for (const [excludeParam, field] of Object.entries(OLD_EXCLUDE_PARAM_TO_FIELD)) {
    const value = params.get(excludeParam);
    if (value) {
      const excludeValues = value.split(',').filter(Boolean);
      if (filters[field].values.length > 0) {
        // Mixed state: include wins, drop excludes (lossy migration)
        continue;
      }
      filters[field] = { values: excludeValues, mode: 'exclude' };
    }
  }

  // Old-format plural exclude aliases
  for (const [pluralExclude, singularExclude] of Object.entries(OLD_EXCLUDE_ALIASES)) {
    const field = OLD_EXCLUDE_PARAM_TO_FIELD[singularExclude];
    if (filters[field].values.length === 0) {
      const value = params.get(pluralExclude);
      if (value) {
        filters[field] = { values: value.split(',').filter(Boolean), mode: 'exclude' };
      }
    }
  }

  // Emergency is boolean
  filters.emergency = params.get('emergency') === 'true';

  return filters;
}

/**
 * Serialize MessageFilters to URLSearchParams.
 *
 * Note: persists _mode=exclude even when values are empty (to support presetting
 * mode before adding values). This is intentionally asymmetric with filtersToApiParams,
 * which skips empty fields — URL state preserves user intent, API calls only send
 * actual filter criteria.
 */
export function filtersToParams(filters: MessageFilters): URLSearchParams {
  const params = new URLSearchParams();

  for (const field of FILTER_FIELDS) {
    const { values, mode } = filters[field];
    const urlParam = FIELD_TO_URL_PARAM[field];

    // Strip client-only sentinels from bookmarkable URLs
    const cleaned = values.filter((v) => v !== '__empty_selection__');
    if (cleaned.length > 0) {
      params.set(urlParam, cleaned.join(','));
    }
    // Persist exclude mode even when values are empty (preset before adding values)
    if (mode === 'exclude') {
      params.set(`${urlParam}_mode`, 'exclude');
    }
  }

  if (filters.emergency) {
    params.set('emergency', 'true');
  }

  return params;
}

/** All known URL param names — used to clean stale params before writing new filter state. */
const ALL_URL_KEYS: string[] = (() => {
  const keys: string[] = [];
  for (const param of Object.values(FIELD_TO_URL_PARAM)) {
    keys.push(param, `${param}_mode`);
  }
  for (const alias of Object.keys(URL_PARAM_ALIASES)) {
    keys.push(alias);
  }
  for (const param of Object.keys(OLD_EXCLUDE_PARAM_TO_FIELD)) {
    keys.push(param);
  }
  for (const alias of Object.keys(OLD_EXCLUDE_ALIASES)) {
    keys.push(alias);
  }
  keys.push('emergency');
  return keys;
})();

/** Map filters to server API query params (existing dual-array format). */
export function filtersToApiParams(filters: MessageFilters): Record<string, string> {
  const apiParams: Record<string, string> = {};

  for (const field of FILTER_FIELDS) {
    const { values, mode } = filters[field];
    // Strip client-only sentinels so they never reach the server
    const cleaned = values.filter((v) => v !== '__empty_selection__');
    if (cleaned.length === 0) continue;

    if (mode === 'include') {
      apiParams[FIELD_TO_API_INCLUDE[field]] = cleaned.join(',');
    } else {
      const excludeKey = FIELD_TO_API_EXCLUDE[field];
      if (excludeKey) {
        apiParams[excludeKey] = values.join(',');
      }
    }
  }

  if (filters.emergency) {
    apiParams.emergency_only = 'true';
  }

  return apiParams;
}

// =============================================================================
// useMessageFiltersController — storage-agnostic filter handlers
// =============================================================================

/**
 * The shape every useMessageFilters variant returns. Both the URL-backed and
 * local-state variants must return exactly this so consumers (Live/Review pages,
 * SharedFilterBlock, AlertEditor) are interchangeable across storage backends.
 */
export interface MessageFiltersController {
  filters: MessageFilters;
  addFilter: (field: FilterField, value: string) => void;
  removeFilter: (field: FilterField, value: string) => void;
  setField: (field: FilterField, values: string[], mode?: 'include' | 'exclude') => void;
  updateFilters: (newFilters: MessageFilters) => void;
  toggleFieldMode: (field: FilterField) => void;
  clearField: (field: FilterField) => void;
  clearAll: () => void;
  hasActiveFilters: boolean;
}

/**
 * Core filter handlers shared by every variant. Takes the current `filters` and
 * a storage-agnostic `updateFilters` writer; derives all mutation handlers from
 * them. The writer's contract is a plain value: `updateFilters(nextFilters)` —
 * matching the original URL-backed hook. Handler bodies are unchanged from the
 * original useMessageFilters implementation.
 */
function useMessageFiltersController(
  filters: MessageFilters,
  updateFilters: (newFilters: MessageFilters) => void
): MessageFiltersController {
  const addFilter = useCallback(
    (field: FilterField, value: string) => {
      const fieldFilter = filters[field];
      if (fieldFilter.values.includes(value)) return;

      const next = { ...filters };
      next[field] = { ...fieldFilter, values: [...fieldFilter.values, value] };
      updateFilters(next);
    },
    [filters, updateFilters]
  );

  const removeFilter = useCallback(
    (field: FilterField, value: string) => {
      const fieldFilter = filters[field];
      const newValues = fieldFilter.values.filter((v) => v !== value);

      const next = { ...filters };
      // Reset mode to include when removing last value
      next[field] =
        newValues.length === 0 ? { ...DEFAULT_FIELD } : { ...fieldFilter, values: newValues };
      updateFilters(next);
    },
    [filters, updateFilters]
  );

  const toggleFieldMode = useCallback(
    (field: FilterField) => {
      // Only toggle fields that support exclude
      if (!TOGGLEABLE_FIELDS.has(field)) return;

      const next = { ...filters };
      next[field] = {
        ...filters[field],
        mode: filters[field].mode === 'include' ? 'exclude' : 'include'
      };
      updateFilters(next);
    },
    [filters, updateFilters]
  );

  /** Set all values for a field at once (single update) */
  const setField = useCallback(
    (field: FilterField, values: string[], mode: 'include' | 'exclude' = 'include') => {
      const next = { ...filters };
      next[field] = values.length > 0 ? { values, mode } : { ...DEFAULT_FIELD };
      updateFilters(next);
    },
    [filters, updateFilters]
  );

  const clearField = useCallback(
    (field: FilterField) => {
      const next = { ...filters };
      next[field] = { ...DEFAULT_FIELD };
      updateFilters(next);
    },
    [filters, updateFilters]
  );

  const clearAll = useCallback(() => {
    updateFilters(createDefaultFilters());
  }, [updateFilters]);

  const hasActiveFilters = useMemo(() => {
    return FILTER_FIELDS.some((f) => filters[f].values.length > 0) || filters.emergency;
  }, [filters]);

  return {
    filters,
    addFilter,
    removeFilter,
    setField,
    updateFilters,
    toggleFieldMode,
    clearField,
    clearAll,
    hasActiveFilters
  };
}

// =============================================================================
// useMessageFilters hook — URL-backed (search params)
// =============================================================================

export function useMessageFilters(): MessageFiltersController {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => parseFiltersFromParams(searchParams), [searchParams]);

  const updateFilters = useCallback(
    (newFilters: MessageFilters) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          // Remove all known filter params
          for (const key of ALL_URL_KEYS) {
            next.delete(key);
          }

          // Write new filter params
          const filterParams = filtersToParams(newFilters);
          for (const [key, value] of filterParams.entries()) {
            next.set(key, value);
          }

          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return useMessageFiltersController(filters, updateFilters);
}

// =============================================================================
// useLocalMessageFilters hook — in-memory state (no Router required)
// =============================================================================

/**
 * Local-state variant of useMessageFilters. Holds the filter state in React
 * useState instead of the URL, so it can be used outside a Router (e.g. inside a
 * modal mounted within the app's BrowserRouter, where nesting a second Router is
 * forbidden). Returns the exact same shape as useMessageFilters; consumers can
 * seed it by calling the returned `updateFilters`.
 */
export function useLocalMessageFilters(initial?: MessageFilters): MessageFiltersController {
  const [filters, setFilters] = useState<MessageFilters>(() => initial ?? createDefaultFilters());

  const updateFilters = useCallback((newFilters: MessageFilters) => {
    setFilters(newFilters);
  }, []);

  return useMessageFiltersController(filters, updateFilters);
}
